/**
 * DTC Services API Client
 * Direct communication with DTC-Services REST API (replaces Gatekeeper proxy)
 */

import type {
    AuthResponse,
    LoginResponse,
    PartnerLiveResponse,
    SDKSettings,
    SessionData,
    UserDetailsFormData,
    ValidEmailResponse,
} from '../store/types';
import { getSessionCookie, removeSessionCookie, setSessionCookie } from './cookies';

export { removeSessionCookie as clearSession, getSessionCookie as getSession, setSessionCookie as setSession };

export interface DtcApiConfig {
    baseUrl: string;
    timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 60_000;

class DtcApiClient {
    private config: DtcApiConfig;
    private sessionData: SessionData | null = null;

    constructor(config: DtcApiConfig) {
        this.config = config;
    }

    setSessionData(session: SessionData | null): void {
        this.sessionData = session;
    }

    private getSession(): SessionData | null {
        return this.sessionData || getSessionCookie();
    }

    private async post<T>(endpoint: string, body: Record<string, unknown>): Promise<T> {
        const controller = new AbortController();
        const timeoutMs = this.config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        let response: Response;
        try {
            response = await fetch(`${this.config.baseUrl}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
                signal: controller.signal,
            });
        } catch (err) {
            clearTimeout(timeoutId);
            if (err instanceof DOMException && err.name === 'AbortError') {
                throw new Error(`Request timed out after ${timeoutMs}ms`);
            }
            throw err;
        } finally {
            clearTimeout(timeoutId);
        }

        if (!response.ok) {
            const text = await response.text();
            let parsed: Record<string, unknown>;
            try {
                parsed = JSON.parse(text);
            } catch {
                throw new Error(`Request failed: ${response.status}`);
            }
            if (parsed.error) {
                throw Object.assign(
                    new Error((parsed.message as string) || `Request failed: ${response.status}`),
                    parsed,
                );
            }
            throw new Error((parsed.message as string) || `Request failed: ${response.status}`);
        }

        const responseText = await response.text();
        if (!responseText || responseText.trim() === '') {
            return { status: 'success' } as T;
        }

        return JSON.parse(responseText) as T;
    }

    private buildSessionBody(extra: Record<string, unknown> = {}): Record<string, unknown> {
        const session = this.getSession();
        if (!session) {
            throw new Error('No active session');
        }
        return {
            session_id: session.session_id,
            session_token: session.session_token,
            ...extra,
        };
    }

    async login(accessToken: string, pin: string): Promise<LoginResponse> {
        const response = await this.post<LoginResponse>('/storefront/login', {
            accessToken: accessToken,
            passCode: pin,
        });

        if (response && !response.error && response.session_id && response.session_token) {
            setSessionCookie({
                session_id: response.session_id,
                session_token: response.session_token,
            });

            this.setSessionData({
                session_id: response.session_id,
                session_token: response.session_token,
            });
        }

        return response;
    }

    async authenticate(sessionId: string, sessionToken: string): Promise<AuthResponse> {
        this.setSessionData({
            session_id: sessionId,
            session_token: sessionToken,
        });

        return this.post<AuthResponse>('/storefront/auth', {
            session_id: sessionId,
            session_token: sessionToken,
        });
    }

    async isPartnerLive(): Promise<PartnerLiveResponse> {
        return this.post<PartnerLiveResponse>('/storefront/is-partner-live', this.buildSessionBody());
    }

    async checkValidEmail(): Promise<ValidEmailResponse> {
        return this.post<ValidEmailResponse>('/storefront/check-valid-email', this.buildSessionBody());
    }

    async updateEmail(email: string): Promise<{ error?: string }> {
        return this.post<{ error?: string }>('/storefront/update-email', this.buildSessionBody({ email }));
    }

    async updateUserDetails(formData: UserDetailsFormData): Promise<{ error?: string }> {
        return this.post<{ error?: string }>(
            '/storefront/update-email',
            this.buildSessionBody({
                user_first_name: formData.firstName,
                user_last_name: formData.lastName,
                user_email: formData.email,
                user_zip_code: formData.zipCode,
                user_phone_number: formData.phoneNumber,
                user_terms: formData.acceptTerms,
            }),
        );
    }

    async getSDKSettings(): Promise<SDKSettings & { error?: string }> {
        return this.post<SDKSettings & { error?: string }>('/storefront/sdk-settings', this.buildSessionBody());
    }

    async resetPin(accessToken: string): Promise<{ status?: string; error?: boolean; message?: string }> {
        return this.post<{ status?: string; error?: boolean; message?: string }>('/storefront/reset-pin', {
            accessToken: accessToken,
        });
    }

    logout(): void {
        removeSessionCookie();
        this.setSessionData(null);
    }
}

const defaultConfig: DtcApiConfig = {
    baseUrl: process.env.DTC_SERVICES_URL || 'http://localhost:8068',
};

export const dtcApi = new DtcApiClient(defaultConfig);

export { DtcApiClient };
